import React from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Video,
  FileText,
  Tags,
  Settings,
  LogOut,
  Users,
  Shield,
  Building2,
  Megaphone,
  Calendar,
  FolderOpen,
  Award,
  Target,
  Layers,
  Mail,
  FlaskConical,
  LayoutDashboard,
  ClipboardList,
  Tag,
  HelpCircle,
  ClipboardCheck,
  ScrollText,
} from "lucide-react";
import { useAuth } from "@/contexts/use-auth";
import logoUrl from "@assets/logo_square_1753226114988.png";

export default function Sidebar() {
  const [location] = useLocation();
  const { signOut, canAccessPage } = useAuth();

  const allNavigationItems = [
    { path: "/sales-dashboard", label: "Dashboard", icon: LayoutDashboard, page: "sales-dashboard" },
    { path: "/", label: "Videos", icon: Video, page: "videos" },
    { path: "/series", label: "Series", icon: Layers, page: "series" },
    { path: "/articles", label: "Articles", icon: FileText, page: "articles" },
    {
      path: "/categories",
      label: "Categories",
      icon: Tags,
      page: "categories",
    },
    { path: "/contributors", label: "Contributors", icon: Users, page: "authors" },
    {
      path: "/promotions",
      label: "Promotions",
      icon: Megaphone,
      page: "promotions",
    },
    { path: "/events", label: "Events", icon: Calendar, page: "events" },
    {
      path: "/programs",
      label: "Programs",
      icon: FolderOpen,
      page: "programs",
    },
    { path: "/badges", label: "Badges", icon: Award, page: "badges" },
    {
      path: "/milestones",
      label: "Milestones",
      icon: Target,
      page: "milestones",
    },
    {
      path: "/newsletters",
      label: "Newsletters",
      icon: Mail,
      page: "newsletters",
    },
    {
      path: "/customers",
      label: "Customers / Partners",
      icon: Building2,
      page: "customers",
    },
    {
      path: "/trials",
      label: "Trials",
      icon: FlaskConical,
      page: "trials",
    },
    {
      path: "/company-audit",
      label: "Company Audit",
      icon: ClipboardList,
      page: "company-audit",
    },
    {
      path: "/user-management",
      label: "User Management",
      icon: Shield,
      page: "user-management",
    },
    { path: "/policies", label: "Policies", icon: ScrollText, page: "policies" },
    { path: "/settings", label: "Settings", icon: Settings, page: "settings" },
    {
      path: "/pulse-surveys/categories",
      label: "Categories",
      icon: Tag,
      page: "pulse-survey-categories",
    },
    {
      path: "/pulse-surveys/questions",
      label: "Questions",
      icon: HelpCircle,
      page: "pulse-survey-questions",
    },
    {
      path: "/pulse-surveys/templates",
      label: "Survey Templates",
      icon: ClipboardCheck,
      page: "pulse-survey-templates",
    },
  ];

  // Filter navigation items based on user permissions
  const navigationItems = allNavigationItems.filter((item) =>
    canAccessPage(item.page),
  );

  const appContentItems = navigationItems.filter((item) =>
    [
      "videos",
      "series",
      "articles",
      "categories",
      "authors",
      "promotions",
      "events",
      "programs",
      "badges",
      "milestones",
    ].includes(item.page),
  );

  const adminItems = navigationItems.filter((item) =>
    ["sales-dashboard", "newsletters", "customers", "trials", "company-audit", "user-management", "policies", "settings"].includes(item.page),
  );

  const pulseSurveyItems = navigationItems.filter((item) =>
    ["pulse-survey-categories", "pulse-survey-questions", "pulse-survey-templates"].includes(item.page),
  );

  const isActive = (path: string) => {
    if (path === "/") {
      return location === "/" || location === "/dashboard";
    }
    return location.startsWith(path);
  };

  return (
    <div className="w-64 bg-white border-r border-slate-200 h-screen flex flex-col overflow-y-auto">
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center space-x-3">
          <img src={logoUrl} alt="Wellbound" className="w-8 h-8" />
          <div>
            <h1 className="text-lg font-bold text-slate-900">Wellbound</h1>
            <p className="text-xs text-slate-600">Content Manager</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <div className="space-y-6">
          {/* App Content Section */}
          {appContentItems.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                App Content
              </h3>
              <div className="space-y-1">
                {appContentItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.path} href={item.path}>
                      <Button
                        variant={isActive(item.path) ? "default" : "ghost"}
                        className="w-full justify-start"
                        size="sm"
                      >
                        <Icon className="w-4 h-4 mr-3" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Admin Section */}
          {adminItems.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Admin
              </h3>
              <div className="space-y-1">
                {adminItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.path} href={item.path}>
                      <Button
                        variant={isActive(item.path) ? "default" : "ghost"}
                        className="w-full justify-start"
                        size="sm"
                      >
                        <Icon className="w-4 h-4 mr-3" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pulse Surveys Section */}
          {pulseSurveyItems.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Pulse Surveys
              </h3>
              <div className="space-y-1">
                {pulseSurveyItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.path} href={item.path}>
                      <Button
                        variant={isActive(item.path) ? "default" : "ghost"}
                        className="w-full justify-start"
                        size="sm"
                      >
                        <Icon className="w-4 h-4 mr-3" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Sign Out */}
      <div className="p-4 border-t border-slate-200">
        <Button
          variant="ghost"
          size="sm"
          onClick={signOut}
          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <LogOut className="w-4 h-4 mr-3" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}
